import {
  safeGetLocalStorageItem,
  safeSetLocalStorageItem,
} from '@mc/browser-storage'

export type LoginMethod = 'google' | 'facebook' | 'apple' | 'email'

const LAST_LOGIN_METHOD_KEY = 'mc_last_login_method'
const AUTH_ATTEMPT_FLAG_KEY = 'mc_auth_attempt'

export const mapCurrentAuthTypeToLoginMethod = (
  currentAuthType: string | undefined,
): LoginMethod | null => {
  if (!currentAuthType) return null

  switch (currentAuthType.toLowerCase()) {
    case 'google_oauth2':
      return 'google'
    case 'facebook':
      return 'facebook'
    case 'apple':
      return 'apple'
    case 'identity':
    case 'email':
      return 'email'
    default:
      return null
  }
}

export const clearLastLoginMethod = (): void => {
  if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
    try {
      localStorage.removeItem(LAST_LOGIN_METHOD_KEY)
    } catch (err) {
      // Silently fail - same pattern as other safe storage utilities
    }
  }
}

export const setAuthAttemptFlag = (attemptType: 'login' | 'signup'): void => {
  if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.setItem(AUTH_ATTEMPT_FLAG_KEY, attemptType)
    } catch (err) {
      // Silently fail - sessionStorage might be disabled or full
    }
  }
}

export const hasAuthAttemptFlag = (
  attemptType: 'login' | 'signup',
): boolean => {
  if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
    try {
      return sessionStorage.getItem(AUTH_ATTEMPT_FLAG_KEY) === attemptType
    } catch (err) {
      // Silently fail - sessionStorage might be disabled
    }
  }
  return false
}

export const clearAuthAttemptFlag = (): void => {
  if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.removeItem(AUTH_ATTEMPT_FLAG_KEY)
    } catch (err) {
      // Silently fail - sessionStorage might be disabled
    }
  }
}

export const setLastLoginMethod = (method: LoginMethod): void => {
  safeSetLocalStorageItem(LAST_LOGIN_METHOD_KEY, method)
}

export const getLastLoginMethod = (): LoginMethod | null => {
  const method = safeGetLocalStorageItem(LAST_LOGIN_METHOD_KEY)

  // Validate that the stored value is a valid LoginMethod
  if (method && ['google', 'facebook', 'apple', 'email'].includes(method)) {
    return method as LoginMethod
  }

  return null
}

export const updateLastLoginMethod = (
  currentAuthType?: string,
): LoginMethod | null => {
  const currentLoginMethod = mapCurrentAuthTypeToLoginMethod(currentAuthType)
  const lastLoginMethod = getLastLoginMethod()

  if (currentLoginMethod && lastLoginMethod !== currentLoginMethod) {
    setLastLoginMethod(currentLoginMethod)
  }

  return currentLoginMethod
}
